/**
 * useReportAbandonedBelongings — cleaner-side mutation that calls the
 * report-abandoned-belongings Edge Function. Surfaces structured error
 * codes from the Edge Function + RPC as AppError.code so the inline
 * blocker UI can render them via the standard pattern (CLAUDE.md
 * ERROR 4).
 *
 * Mirrors useRequestAdditionalFee so the contract-layer cleaner-side
 * surfaces stay shaped the same way.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

export interface ReportAbandonedBelongingsVars {
  cleaningTaskId:     string
  description:        string
  photoStoragePaths?: string[]
}

export interface ReportAbandonedBelongingsResult {
  report_id:        string
  status:           'reported'
  cleaning_task_id: string
  room_id:          string
}

export function useReportAbandonedBelongings(): UseMutationResult<ReportAbandonedBelongingsResult, AppError, ReportAbandonedBelongingsVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<ReportAbandonedBelongingsResult, AppError, ReportAbandonedBelongingsVars>({
    mutationFn: async (vars): Promise<ReportAbandonedBelongingsResult> => {
      const { data, error } = await supabase.functions.invoke<ReportAbandonedBelongingsResult>('report-abandoned-belongings', {
        body: {
          cleaning_task_id:     vars.cleaningTaskId,
          description:          vars.description,
          photo_storage_paths:  vars.photoStoragePaths,
        },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'REQUEST_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Report returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['abandoned-belongings'] })
      void queryClient.invalidateQueries({ queryKey: ['abandoned-belongings', vars.cleaningTaskId] })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_DESCRIPTION':  return 'Provide a description between 1 and 1,000 characters.'
    case 'INVALID_PHOTO_PATHS':  return 'Photo references must be non-empty strings under 500 characters.'
    case 'TOO_MANY_PHOTOS':      return 'At most 10 photos can be attached to a single report.'
    case 'FORBIDDEN_ROLE':       return 'Only cleaners can file an abandoned-belongings report.'
    case 'FORBIDDEN_OWNERSHIP':  return 'You can only file a report for a task assigned to you.'
    case 'FORBIDDEN_ORG':        return 'This task does not belong to your organization.'
    case 'INVALID_STATE':        return 'This task is not in a state where a report can be filed.'
    case 'TASK_NOT_FOUND':       return 'This task could not be found.'
    case 'FEATURE_DISABLED':     return 'Abandoned-belongings tracking is not enabled for this organization.'
    case 'RATE_LIMITED':         return 'Too many reports in a row. Try again in a moment.'
    default:                     return detail || 'Could not file the report.'
  }
}
